import React, { useMemo } from 'react';
import Icon from '@/components/ui/Icon';
import GradeGroupSection from './GradeGroupSection';
import StandardsErrorBoundary from './StandardsErrorBoundary';
import {
  ChildrenMap,
  SearchResult,
  countMatchesInSubtree,
  groupByGrade,
} from './standardsTreeUtils';
import { StandardNode } from './types';

interface StandardsTreeProps {
  childrenMap: ChildrenMap;
  correlationCountMap: Map<number, number>;
  expandedGrades: Set<string>;
  expandedNodes: Set<number>;
  gradeFilter: string;
  searchQuery: string;
  searchResult: SearchResult;
  showCorrelationCounts: boolean;
  standards: StandardNode[];
  onClearSearch?: () => void;
  onToggleGrade: (grade: string) => void;
  onToggleNode: (id: number) => void;
}

const StandardsTreeInner = ({
  childrenMap,
  correlationCountMap,
  expandedGrades,
  expandedNodes,
  gradeFilter,
  searchQuery,
  searchResult,
  showCorrelationCounts,
  standards,
  onClearSearch,
  onToggleGrade,
  onToggleNode,
}: StandardsTreeProps) => {
  const gradeGroups = useMemo(() => groupByGrade(standards, childrenMap), [standards, childrenMap]);
  const isSearching = searchQuery.trim().length > 0;
  const { matchIds, visibleIds } = searchResult;

  let filtered = gradeFilter
    ? gradeGroups.filter(g => g.grade === gradeFilter)
    : gradeGroups;

  if (isSearching) {
    filtered = filtered.filter(g => g.roots.some(r => visibleIds.has(r.id)));
  }

  if (filtered.length === 0) {
    return (
      <div className="tw-text-center tw-py-12 tw-font-tci">
        {isSearching && (
          <Icon className="tw-text-grey tw-mb-4 tw-mx-auto tw-block" icon="Search" size={28} />
        )}
        <p className="tw-font-bold tw-text-sm tw-mb-1">
          {isSearching ? 'No standards match these filters.' : 'No standards found for the selected grade.'}
        </p>
        {isSearching && (
          <>
            <p className="tw-text-sm tw-text-dark-grey tw-mb-4">
              Try a different search term, switch state, or clear filters.
            </p>
            {onClearSearch && (
              <button
                className="btn btn--outline-purple tw-px-4 tw-text-sm"
                type="button"
                onClick={onClearSearch}
              >
                Clear search
              </button>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="tw-font-tci">
      {filtered.map((group) => {
        const matchCount = isSearching
          ? group.roots.reduce(
            (acc, r) => acc + countMatchesInSubtree(r.id, childrenMap, matchIds),
            0,
          )
          : 0;
        const correlationCount = group.roots.reduce(
          (acc, r) => acc + (correlationCountMap.get(r.id) ?? 0),
          0,
        );

        return (
          <GradeGroupSection
            key={group.grade}
            childrenMap={childrenMap}
            correlationCount={correlationCount}
            correlationCountMap={correlationCountMap}
            expandedNodes={expandedNodes}
            group={group}
            isOpen={expandedGrades.has(group.grade)}
            matchCount={matchCount}
            matchIds={isSearching ? matchIds : undefined}
            searchQuery={searchQuery}
            showCorrelationCounts={showCorrelationCounts}
            visibleIds={isSearching ? visibleIds : undefined}
            onToggleGrade={onToggleGrade}
            onToggleNode={onToggleNode}
          />
        );
      })}
    </div>
  );
};

const StandardsTree = (props: StandardsTreeProps) => (
  <StandardsErrorBoundary>
    <StandardsTreeInner {...props} />
  </StandardsErrorBoundary>
);

export default StandardsTree;
